import moment from 'moment';
import { FinancialStatementFullWrite, FinancialStatementPeriodFullWrite, MeasurementDate } from 'api';
import { FinancialStatementPeriod } from 'common/types/financials';
import { ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { template as balanceTemplate } from 'pages/Financials/balance-sheet/data';
import { reverseParser as balanceReverseParser } from 'pages/Financials/balance-sheet/utilities';
import { reverseParser as incomeReverseParser } from 'pages/Financials/income-statement/utilities';
import kpiReverseParser from 'pages/Financials/kpi/config/reverseParser';
import { generateKeyForMap, getMapKeyFromIncomeStatement } from './auxiliaryFunctions';
import { FinancialPeriodColumn, FinancialsStatementData } from './types';
import { KpiReverseParserType } from '../kpi/config/types';
/**
 *
 * @param {*} incomeStatement
 * @param {*} balanceSheets
 * @param {*} financialStatement
 * @param {*} periods
 * @returns
 */
const getFinancialsData = (
  incomeStatement: ScalarSpreadsheetConfigDataInterface,
  balanceSheets: ScalarSpreadsheetConfigDataInterface,
  KPIs: KpiReverseParserType,
  financialStatement: FinancialStatementPeriod,
  periods: FinancialPeriodColumn[],
  selectedMeasurementDate: MeasurementDate
): FinancialsStatementData => {
  /* periods don't come sorted as their appear in render => use map */
  const periodsMap = new Map();
  periods.forEach(period => {
    const { period_type, statement_date } = period;
    const keyToSet = generateKeyForMap(period_type!, statement_date!);
    periodsMap.set(keyToSet, period);
  });
  const updatedIncomeStatementColumns = incomeReverseParser(incomeStatement);
  const updatedBalanceSheetsColumns = balanceReverseParser(balanceSheets);
  const updatedKPIs = kpiReverseParser(KPIs);
  /* this variable is created to check when template or prexisting data
  should be used to fill missing NTM periods since updatedBalanceSheets
  is shorter than updatedIncomeStatements */
  const updBalanceSheetLength = updatedBalanceSheetsColumns.length;
  const updKPILength = updatedKPIs.length;

  const tmpFinancialStatement = { ...financialStatement } as unknown as FinancialStatementFullWrite;
  const financialStatementPeriods: FinancialStatementPeriod[] = [];

  updatedIncomeStatementColumns.forEach((col, index) => {
    const financialStatementPeriod = tmpFinancialStatement.financial_statement_periods.find(period => {
      const matchByPeriodId =
        col.income_statement?.financial_statement_period &&
        period.id === col.income_statement.financial_statement_period;
      const matchByDateAndPeriodType =
        period.statement_date === periods[index].statement_date &&
        period.period_type === periods[index].period_type &&
        period.id === col.income_statement?.financial_statement_period;
      return matchByPeriodId || matchByDateAndPeriodType;
    });

    // remove de uuid from visibleCols id
    delete updatedIncomeStatementColumns[index].id;
    if (index < updBalanceSheetLength) {
      delete updatedBalanceSheetsColumns[index].id;
    }

    if (financialStatementPeriod) {
      // the 2nd part is intended for existing data for NTM periods.
      const balanceUpdate =
        index < updBalanceSheetLength
          ? {
            ...financialStatementPeriod.balance_sheet,
            ...updatedBalanceSheetsColumns[index].balance_sheet,
          }
          : { ...balanceTemplate, ...financialStatementPeriod.balance_sheet, name: 'NTM' };

      const kpiUpdate = index < updKPILength ? updatedKPIs[index]?.kpi_dataset || [] : [];

      // a period already exist
      // update the already existing period
      financialStatementPeriods.push({
        ...financialStatementPeriod,
        income_statement: {
          ...financialStatementPeriod.income_statement,
          ...col.income_statement,
        },
        statement_date:
          financialStatementPeriod.income_statement.reported_statement_date ?? financialStatementPeriod.statement_date,
        balance_sheet: balanceUpdate,
        kpi_dataset: kpiUpdate,
        is_projection: moment(selectedMeasurementDate.date).isBefore(
          financialStatementPeriod.income_statement.reported_statement_date ?? financialStatementPeriod.statement_date
        ),
      });
    } else {
      // add a new period to financial statement
      const generatedKey = getMapKeyFromIncomeStatement(col);
      const matchedPeriod = periodsMap.get(generatedKey);
      financialStatementPeriods.push({
        income_statement: { ...col.income_statement },
        // any as the spread supposedly always overrides the template
        balance_sheet: { ...balanceTemplate, name: 'NTM', ...updatedBalanceSheetsColumns[index].balance_sheet },
        is_projection: matchedPeriod.is_projection,
        statement_date: matchedPeriod.statement_date,
        period_type: matchedPeriod.period_type,
        name: matchedPeriod.name,
        kpi_dataset: updatedKPIs[index]?.kpi_dataset || [],
        column_ref: matchedPeriod.id,
      });
    }
  });

  tmpFinancialStatement.financial_statement_periods = financialStatementPeriods as FinancialStatementPeriodFullWrite[];
  return tmpFinancialStatement;
};

export default getFinancialsData;
